import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "../components/layout/Footer";
import Header from "../components/layout/Header";
import Hero from "../components/sections/Hero";

// Lazy load remaining sections
const TabibiAI = lazy(() => import("../components/sections/TabibiAI"));
const CoreFeatures = lazy(() => import("../components/sections/CoreFeatures"));
const TabibiAppsSection = lazy(() => import("../components/sections/TabibiAppsSection"));
const IntegrationsSection = lazy(() => import("../components/sections/IntegrationsSection"));
const OnlineBooking = lazy(() => import("../components/sections/OnlineBooking"));
const PainSolution = lazy(() => import("../components/sections/PainSolution"));
const Pricing = lazy(() => import("../components/sections/Pricing"));
const Testimonials = lazy(() => import("../components/sections/Testimonials"));
const FAQ = lazy(() => import("../components/sections/FAQ"));
const BlogSection = lazy(() => import("../components/sections/BlogSection"));
const CTA = lazy(() => import("../components/sections/CTA"));

// Loading skeletons for lazy components
function SectionSkeleton() {
  return (
    <div className="container py-16">
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
        <div className="grid md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-48 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LazySection({ children, fallback = <SectionSkeleton />, rootMargin = "600px", id }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isVisible) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return (
    <div ref={ref} id={id}>
      {isVisible ? children : fallback}
    </div>
  );
}

export default function Landing() {
  const location = useLocation();

  // Scroll to hash on load/change
  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.replace("#", "");
    let attempts = 0;

    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        // Scroll to the beginning of the section (no offset needed since header is not sticky)
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempts < 6) {
        attempts += 1;
        setTimeout(tryScroll, 200);
      }
    };

    // small timeout to allow layout/lazy sections to mount
    setTimeout(tryScroll, 50);
  }, [location]);

  return (
    // Make sure the main container allows normal scrolling and accounts for fixed header
    <div dir="rtl" className="min-h-svh bg-background relative">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:start-4 focus:z-50 focus:bg-background focus:text-foreground focus:px-4 focus:py-2 focus:rounded-[var(--radius)]"
      >
        تخطي إلى المحتوى
      </a>
      <Helmet>
        <title>تابيبي — نظام إدارة العيادات والمواعيد</title>
        <meta name="description" content="تابيبي نظام عربي لإدارة العيادات: حجز مواعيد، ملف طبي، فواتير، وتقارير." />
        <link rel="canonical" href="https://tabibi.site/" />
        <meta property="og:title" content="تابيبي — نظام إدارة العيادات والمواعيد" />
        <meta property="og:description" content="تابيبي نظام عربي لإدارة العيادات: حجز مواعيد، ملف طبي، فواتير، وتقارير." />
        <meta property="og:image" content="https://tabibi.site/hero-optimized.webp" />
        <meta property="og:url" content="https://tabibi.site/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="تابيبي — نظام إدارة العيادات والمواعيد" />
        <meta name="twitter:description" content="تابيبي نظام عربي لإدارة العيادات: حجز مواعيد، ملف طبي، فواتير، وتقارير." />
        <meta name="twitter:image" content="https://tabibi.site/hero-optimized.webp" />
      </Helmet>

      <div className="pointer-events-none fixed inset-0 -z-10 opacity-70">
        <div className="absolute -top-24 start-1/2 -translate-x-1/2 size-[40rem] rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute top-1/3 end-0 size-[24rem] rounded-full bg-secondary/20 blur-3xl" />
      </div>
      
      {/* Header is now fixed and dynamically moves with scroll */}
      <Header />
      <main id="main-content">
        <Hero />

        <LazySection id="tabibi-ai">
          <Suspense fallback={<SectionSkeleton />}>
            <TabibiAI />
          </Suspense>
        </LazySection>

        <LazySection id="core-features">
          <Suspense fallback={<SectionSkeleton />}>
            <CoreFeatures />
          </Suspense>
        </LazySection>

        <LazySection>
          <Suspense fallback={<SectionSkeleton />}>
            <TabibiAppsSection />
          </Suspense>
        </LazySection>

        <LazySection id="online-booking">
          <Suspense fallback={<SectionSkeleton />}>
            <OnlineBooking />
          </Suspense>
        </LazySection>

        <LazySection id="integrations">
          <Suspense fallback={<SectionSkeleton />}>
            <IntegrationsSection />
          </Suspense>
        </LazySection>

        <LazySection>
          <Suspense fallback={<SectionSkeleton />}>
            <PainSolution />
          </Suspense>
        </LazySection>

        <div id="pricing">
          <Suspense fallback={<SectionSkeleton />}>
            <Pricing />
          </Suspense>
        </div>

        <LazySection id="testimonials">
          <Suspense fallback={<SectionSkeleton />}>
            <Testimonials />
          </Suspense>
        </LazySection>

        <LazySection id="faq">
          <Suspense fallback={<SectionSkeleton />}>
            <FAQ />
          </Suspense>
        </LazySection>

        <LazySection id="blog">
          <Suspense fallback={<SectionSkeleton />}>
            <BlogSection />
          </Suspense>
        </LazySection>

        <LazySection>
          <Suspense fallback={<SectionSkeleton />}>
            <CTA />
          </Suspense>
        </LazySection>
      </main>
      
      <Footer />
      
      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/+201158954215"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-50 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-all duration-300 transform hover:scale-110"
        aria-label="تواصل معنا عبر واتساب"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}
